class DeliveryUpgrade extends HTMLElement {
    connectedCallback() {
      const params = new URLSearchParams(window.location.search);
      const cutoff = new Date(params.get("cutoff"));
      const now = new Date();
      const deliveryMethod = params.get("delivery_method");
      const orderId = params.get("order");
      const weight = parseFloat(params.get("weight")) * 1000; // convert kg to grams
      const firstName = params.get("first_name") || "";
      const lastName = params.get("last_name") || "";
      const email = params.get("email");
      const phone = params.get("phone");
      const billingAddress = {
        address1: params.get("billing_address1") || "",
        address2: params.get("billing_address2") || "",
        city: params.get("billing_city") || "",
        province: params.get("billing_province") || "",
        country: params.get("billing_country") || "",
        zip: params.get("billing_zip") || ""
      };
        
      if (now > cutoff) {
        this.innerHTML = `<p>Sorry, the cutoff to upgrade your delivery has passed.</p>`;
        return;
      }
  
      const serviceRank = {
        'Swyft Delivery': 1,
        'White Glove': 2,
        'White Glove Plus': 3
      };
  
      const upgrades = [
        {
          name: "Swyft Delivery",
          handle: "swyft-delivery",
          benefits: [
            "Nominated day",
            "2-hour window",
            "Real-time updates",
            "Delivered to your room of choice"
          ]
        },
        {
          name: "White Glove",
          handle: "white-glove",
          benefits: [
            "Nominated day",
            "2-hour window",
            "Real-time updates",
            "Delivered to your room of choice",
            "Assembly",
            "Package removal"
          ]
        },
        {
          name: "White Glove Plus",
          handle: "white-glove-plus",
          benefits: [
            "Nominated day",
            "2-hour window",
            "Real-time updates",
            "Delivered to your room of choice",
            "Assembly",
            "Package removal",
            "Old sofa removal"
          ]
        }
      ];
  
      const currentRank = serviceRank[deliveryMethod];
  
      this.innerHTML = `<div class="upgrade-grid loading"></div>`;
      const container = this.querySelector(".upgrade-grid");
  
      // Add 3 skeleton placeholders to reserve layout
      for (let i = 0; i < 3; i++) {
        const skeleton = document.createElement("div");
        skeleton.className = "upgrade-card skeleton";
        container.appendChild(skeleton);
      }
  
      (async () => {
        const filteredAndSorted = upgrades
          .filter(upgrade => {
            const isCurrent = upgrade.name === deliveryMethod;
            const rank = serviceRank[upgrade.name];
            return isCurrent || rank > currentRank;
          })
          .sort((a, b) => serviceRank[a.name] - serviceRank[b.name]);
  
        const cardsData = await Promise.all(
          filteredAndSorted.map(async (upgrade) => {
            const isCurrent = upgrade.name === deliveryMethod;
            let priceHtml = "";
            let variantId = null;
  
            if (!isCurrent) {
              variantId = this.getVariantId(upgrade.handle, weight);
              if (variantId) {
                const productData = await fetch(`/products/${upgrade.handle}.js`)
                  .then(res => res.ok ? res.json() : null)
                  .catch(() => null);
  
                if (productData) {
                  const variant = productData.variants.find(v => v.id === variantId);
                  if (variant) {
                    const price = (variant.price / 100).toFixed(2);
                    priceHtml = `<div class="upgrade-price">+ £${price}</div>`;
                  }
                }
              }
            }
  
            return { upgrade, isCurrent, variantId, priceHtml };
          })
        );
  
        container.innerHTML = ""; // Remove skeletons
  
        cardsData.forEach(({ upgrade, isCurrent, variantId, priceHtml }, i) => {
          const card = document.createElement("div");
          card.className = `upgrade-card fade-in ${isCurrent ? 'current' : ''}`;
          card.style.transitionDelay = `${i * 100}ms`;
  
          card.innerHTML = `
            <h3>${upgrade.name}</h3>
            <ul>
              ${upgrade.benefits.map(b => `<li><span class="tick">✓</span> ${b}</li>`).join("")}
            </ul>
            ${priceHtml}
            ${
              isCurrent
                ? `<div class="current-badge">Your current delivery method</div>`
                : `<button data-handle="${upgrade.handle}" data-id="${variantId}">Upgrade to ${upgrade.name}</button>`
            }
          `;
  
          container.appendChild(card);
        });
  
        // Reveal the grid with fade-in
        requestAnimationFrame(() => {
          container.classList.remove("loading");
          container.classList.add("ready");
        });
  
        // Button logic
        this.querySelectorAll("button").forEach(button => {
          const variantId = button.dataset.id;
          const handle = button.dataset.handle;
          const [first, ...rest] = name.split(" ");
          const last = rest.join(" ") || "";
  
          button.addEventListener("click", async () => {
            if (!variantId) {
              alert("Sorry, no upgrade option is available for your order weight.");
              return;
            }
          
            // Clear the cart (optional, to avoid stacking)
            await fetch('/cart/clear.js');
          
            // Add the item with line item properties
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: variantId,
                quantity: 1,
                properties: {
                  'Original Order Number': orderId
                }
              })
            });
          
          
            const checkoutParams = new URLSearchParams({
              'checkout[email]': email,
              'checkout[shipping_address][first_name]': firstName,
              'checkout[shipping_address][last_name]': lastName,
              'checkout[shipping_address][phone]': phone,
              'checkout[billing_address][first_name]': firstName,
              'checkout[billing_address][last_name]': lastName,
              'checkout[billing_address][address1]': billingAddress.address1,
              'checkout[billing_address][address2]': billingAddress.address2,
              'checkout[billing_address][city]': billingAddress.city,
              'checkout[billing_address][province]': billingAddress.province,
              'checkout[billing_address][country]': billingAddress.country,
              'checkout[billing_address][zip]': billingAddress.zip,
            });
          
            window.location.href = `/checkout?${checkoutParams.toString()}`;
          });
        });
      })();
    }
  
    getVariantId(upgradeHandle, weightGrams) {
      const weightKg = weightGrams / 1000;
  
      const variantMap = {
        'white-glove': [
          { max: 30, id: 54795273896271 },
          { max: 120, id: 54795273929039 },
          { max: 350, id: 54795273961807 },
          { max: 630, id: 54795273994575 },
          { max: 1130, id: 54795274027343 },
          { max: Infinity, id: 54795274060111 }
        ],
        'white-glove-plus': [
          { max: 30, id: 54795315347791 },
          { max: 120, id: 54795315380559 },
          { max: 350, id: 54795315413327 },
          { max: 630, id: 54795315446095 },
          { max: 1130, id: 54795315478863 },
          { max: Infinity, id: 54795315511631 }
        ]
      };
  
      const variants = variantMap[upgradeHandle];
      if (!variants) return null;
  
      for (const option of variants) {
        if (weightKg <= option.max) {
          return option.id;
        }
      }
  
      return null;
    }
  }
  
  customElements.define('delivery-upgrade', DeliveryUpgrade);